import React from "react"
import {
    StyledModal,
    ModalContent,
    LeftColumn,
    RightColumn,
    ProfileSection,
    StyledAvatar,
    RoleBadge,
    PartyBadge,
    DetailsSection,
    DetailRow,
    DetailLabel,
    DetailValue,
    WebsiteLink,
    SocialLinksSection,
    SocialLink,
} from "QuorumGrassroots/widgets/ReusableComponents/OfficialDetailsModal/OfficialDetailsModal.styles"
import { OfficialDetailsModalProps } from "QuorumGrassroots/widgets/ReusableComponents/OfficialDetailsModal/OfficialDetailsModal.types"
import { XTwitterFarFAIcon } from "QuorumGrassroots/widgets/ReusableComponents/Icons"

// Party label mapping
const PARTY_LABELS: Record<string, string> = {
    D: "DEMOCRAT",
    R: "REPUBLICAN",
    I: "INDEPENDENT",
    Democrat: "DEMOCRAT",
    Republican: "REPUBLICAN",
    Independent: "INDEPENDENT",
}

// Social media platform configuration
const SOCIAL_PLATFORMS = [
    { key: "facebook" as const, icon: "fa-facebook", label: "Facebook" },
    { key: "twitter" as const, icon: "fa-x-twitter", label: "X (Twitter)" },
    { key: "linkedin" as const, icon: "fa-linkedin", label: "LinkedIn" },
    { key: "instagram" as const, icon: "fa-instagram", label: "Instagram" },
]

export const OfficialDetailsModal: React.FC<OfficialDetailsModalProps> = ({ official, isOpen, onClose }) => {
    if (!official) {
        return null
    }

    // Get party label from mapping or uppercase the value
    const getPartyLabel = (party?: string): string => {
        if (!party) return ""
        return PARTY_LABELS[party] || party.toUpperCase()
    }

    // Build social links from official data
    const socialLinks = SOCIAL_PLATFORMS.filter((platform) => official[platform.key]).map((platform) => ({
        ...platform,
        url: official[platform.key]!,
    }))

    return (
        <StyledModal
            opened={isOpen}
            onClose={onClose}
            title={official.full_name}
            centered
            size="xl"
            closeOnClickOutside
            closeOnEscape
            withCloseButton
            overflow="inside"
            transitionProps={{ duration: 200 }}
            keepMounted={false}
        >
            <ModalContent>
                {/* Left Column: Photo, Role, Party, Social Links */}
                <LeftColumn>
                    <ProfileSection>
                        <StyledAvatar src={official.image_url} alt={official.full_name} radius="md">
                            {!official.image_url && official.full_name.charAt(0)}
                        </StyledAvatar>

                        {official.official.role && <RoleBadge>{official.official.role.toUpperCase()}</RoleBadge>}
                        {official.party && <PartyBadge>{getPartyLabel(official.party)}</PartyBadge>}
                    </ProfileSection>

                    {socialLinks.length > 0 && (
                        <SocialLinksSection>
                            {socialLinks.map((link) => (
                                <SocialLink
                                    key={link.icon}
                                    href={link.url}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    aria-label={`Visit ${official.full_name}'s ${link.label} profile`}
                                >
                                    {link.icon === "fa-x-twitter" ? (
                                        <XTwitterFarFAIcon size={18} color="#495057" />
                                    ) : (
                                        <i className={`fa ${link.icon}`} aria-hidden="true" />
                                    )}
                                </SocialLink>
                            ))}
                        </SocialLinksSection>
                    )}
                </LeftColumn>

                {/* Right Column: Contact and Electoral Information */}
                <RightColumn>
                    <DetailsSection>
                        {official.phone && (
                            <DetailRow>
                                <DetailLabel>Phone number</DetailLabel>
                                <DetailValue>{official.phone}</DetailValue>
                            </DetailRow>
                        )}

                        {official.website && (
                            <DetailRow>
                                <DetailLabel>Website</DetailLabel>
                                <WebsiteLink href={official.website} target="_blank" rel="noopener noreferrer">
                                    {official.website}
                                    <i
                                        className="fa fa-external-link"
                                        style={{ marginLeft: "8px", fontSize: "14px" }}
                                        aria-hidden="true"
                                    />
                                </WebsiteLink>
                            </DetailRow>
                        )}

                        {official.address && (
                            <DetailRow>
                                <DetailLabel>Address</DetailLabel>
                                <DetailValue>{official.address}</DetailValue>
                            </DetailRow>
                        )}

                        {official.official.num_terms && (
                            <DetailRow>
                                <DetailLabel>Term</DetailLabel>
                                <DetailValue>{official.official.num_terms}</DetailValue>
                            </DetailRow>
                        )}

                        {official.official.first_election_date && (
                            <DetailRow>
                                <DetailLabel>First elected</DetailLabel>
                                <DetailValue>{official.official.first_election_date}</DetailValue>
                            </DetailRow>
                        )}

                        {official.official.reelection_year && (
                            <DetailRow>
                                <DetailLabel>Next election</DetailLabel>
                                <DetailValue>{official.official.reelection_year}</DetailValue>
                            </DetailRow>
                        )}
                    </DetailsSection>
                </RightColumn>
            </ModalContent>
        </StyledModal>
    )
}
